import React, { useState, useEffect, useCallback } from 'react';
import { S } from '../../styles';
import { API, authHeaders } from '../../utils/helpers';

// ── Settings › Website › Pages: start the website, preview it, connect a
//    domain (1 Nov, multi-shop) ──────────────────────────────────────────────
//
// A shop with no website yet gets "Start your website": a small starter site
// from Settings › Shop (server/helpers/websiteStarter.js). After that this
// card shows the preview address (works before any domain is connected) and
// the shop's domains, with the DNS record to set and a check that the
// domain reaches their site.

async function api(path, opts = {}) {
  const res = await fetch(`${API}/api/website${path}`, {
    credentials: 'include', ...opts,
    headers: { ...authHeaders(!!opts.body), ...(opts.headers || {}) },
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || d.message || `Spinboard answered ${res.status}.`);
  return d;
}

// theirshop.com and www.theirshop.com are one website (the website service
// treats them as the same), so they show as one row: Check asks the first,
// Disconnect removes both.
function groupDomains(list) {
  const rows = [];
  for (const d of list || []) {
    const bare = String(d).replace(/^www\./, '');
    const row = rows.find((r) => r.bare === bare);
    if (row) row.also = d; else rows.push({ bare, host: d, also: null });
  }
  return rows;
}

export default function WebsiteDomains({ T, isDark, btn, sub, onStarted }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [domain, setDomain] = useState('');
  const [checks, setChecks] = useState({});

  const load = useCallback(async () => {
    try { setStatus(await api('/admin/site-status')); setError(''); } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const start = async () => {
    setBusy('start'); setError('');
    try { await api('/admin/site/start', { method: 'POST' }); await load(); onStarted && onStarted(); } catch (e) { setError(e.message); }
    setBusy('');
  };
  const add = async (e) => {
    e.preventDefault();
    setBusy('add'); setError('');
    try { await api('/admin/domains', { method: 'POST', body: JSON.stringify({ domain }) }); setDomain(''); await load(); } catch (err) { setError(err.message); }
    setBusy('');
  };
  const remove = async (host) => {
    if (!window.confirm(`Disconnect ${host.replace(/^www\./, "")} (with and without www)? Visitors to it will no longer see your website.`)) return;
    setBusy(host); setError('');
    try { await api(`/admin/domains/${encodeURIComponent(host)}`, { method: 'DELETE' }); await load(); } catch (err) { setError(err.message); }
    setBusy('');
  };
  const check = async (host) => {
    setBusy(`check:${host}`);
    try {
      const d = await api('/admin/domains/check', { method: 'POST', body: JSON.stringify({ domain: host }) });
      setChecks((c) => ({ ...c, [host]: d }));
      if (d.ok) load();
    } catch (err) { setChecks((c) => ({ ...c, [host]: { ok: false, problem: err.message } })); }
    setBusy('');
  };

  const card = { ...S.settingsCard, background: T.card, border: `1px solid ${T.cardBorder}` };
  const tile = isDark ? '#1e2a40' : '#f4f4ef';
  if (!status) return error ? <div style={card}><div style={{ ...sub, color: '#e74c3c' }}>{error}</div></div> : null;

  if (!status.exists) {
    return (
      <div style={card}>
        <div style={{ ...S.settingsCardTitle, color: T.text }}>Start your website</div>
        <div style={{ ...S.settingsCardDesc, color: T.textSub, marginBottom: 12 }}>
          Spinboard makes a simple starter site from your shop details (Settings › Shop): a home page with your name and the bikes
          you pick, a Visit us page with your address, hours and map, a menu and a footer. Make it your own in the page builder,
          add pages from the page library, then connect your domain. Nothing is public on your own domain until you connect it.
        </div>
        <button style={btn(true)} disabled={!!busy} onClick={start}>{busy === 'start' ? 'Starting…' : 'Start your website'}</button>
        {error && <div style={{ ...sub, color: '#e74c3c', marginTop: 8 }}>{error}</div>}
      </div>
    );
  }

  return (
    <div style={card}>
      <div style={{ ...S.settingsCardTitle, color: T.text }}>Where your website is</div>
      <div style={{ ...S.settingsCardDesc, color: T.textSub, marginBottom: 10 }}>
        {status.previewUrl && <>Preview it any time at <a href={status.previewUrl} target="_blank" rel="noopener noreferrer" style={{ color: T.accent || '#e67e22' }}>{status.previewUrl.replace(/^https?:\/\//, '')}</a>. Google never sees the preview address. </>}
        Connect your own domain below when you are ready for customers to see it.
      </div>

      {groupDomains(status.domains).map(({ host, also }) => {
        const c = checks[host];
        const live = [host, also].includes(status.liveDomain) || (c && c.ok);
        return (
          <div key={host} style={{ background: tile, borderRadius: 8, padding: '9px 12px', marginBottom: 6 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 200px', fontSize: 13.5, fontWeight: 600, color: T.text }}>
                {host}{also && <span style={{ fontWeight: 400, color: T.textSub }}> and {also}</span>}
              </div>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: live ? '#2ecc71' : '#e67e22' }}>{live ? 'Reaching your website' : 'Waiting for DNS'}</span>
              <button style={btn(false)} disabled={!!busy} onClick={() => check(host)}>{busy === `check:${host}` ? 'Checking…' : 'Check'}</button>
              <button style={{ ...btn(false), color: '#e74c3c' }} disabled={!!busy} onClick={() => remove(host)}>Disconnect</button>
            </div>
            {!live && (
              <div style={{ ...sub, marginTop: 6 }}>
                {c && !c.ok && <div style={{ color: '#e67e22', marginBottom: 4 }}>Not yet: {c.problem}.</div>}
                Next, Spinboard adds {host} on its side and sends you one DNS record to set at the company where you bought
                the domain. Once it is set, press Check. DNS changes take from a few minutes to a few hours.
              </div>
            )}
          </div>
        );
      })}

      <form onSubmit={add} style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="www.yourshop.com"
          style={{ flex: '1 1 220px', padding: '7px 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: isDark ? '#0f1726' : '#fff', color: T.text, fontSize: 13 }} />
        <button type="submit" style={btn(true)} disabled={!!busy || !domain.trim()}>{busy === 'add' ? 'Connecting…' : 'Connect a domain'}</button>
      </form>
      {error && <div style={{ ...sub, color: '#e74c3c', marginTop: 8 }}>{error}</div>}
    </div>
  );
}
